import { useInfiniteQuery } from '@tanstack/react-query';
import { Explicit } from '../../components/ui/Explicit';
import { DrawerLink } from '../../app/DrawerLink';
import { Link } from 'react-router-dom';

import { Button } from '../../components/ui/Button';
import { Card, Chip } from '../../components/ui/Card';
import { FavoriteButton } from '../../components/ui/FavoriteButton';
import { RepostButton } from '../../components/ui/RepostButton';
import { AddToPlaylist } from '../library/AddToPlaylist';
import { SkeletonRow } from '../../components/ui/Loader';
import { copy } from '../../config/copy';
import { api } from '../../lib/api';
import { usePlayer } from '../../stores/player';
import type { TrackSummary } from '../tracks/TrackList';
import { useDocumentTitle } from '../../lib/title';
import { ArtistHoverCard } from '../profile/ArtistHoverCard';
import { Verified } from '../../components/ui/Verified';

export interface FeedEvent {
  /** `track` is a release; `repost` is somebody passing it on. */
  kind: 'track' | 'repost';
  at: string;
  caption: string | null;
  actor: { username: string; display_name: string | null; verified?: boolean } | null;
}

export interface FeedTrack extends TrackSummary {
  event: FeedEvent;
}

export interface FeedPageData {
  /** `following` when the viewer follows anybody; `latest` when they do not. */
  mode: 'following' | 'latest';
  following_count: number;
  items: FeedTrack[];
  next_cursor: string | null;
}

export function useFeed() {
  return useInfiniteQuery({
    queryKey: ['feed'],
    queryFn: ({ pageParam }) =>
      // Encoded, even though the server now hands back a URL-safe token. A
      // cursor is opaque, and "it happens not to need escaping today" is how
      // this broke the first time.
      api.get<FeedPageData>(
        `/feed${pageParam ? `?cursor=${encodeURIComponent(pageParam)}` : ''}`,
      ),
    initialPageParam: '' as string,
    /*
     * The server's cursor, verbatim.
     *
     * Never derived from the last item's id here: what the cursor means is the
     * server's business, and a client that reconstructs it is a client that
     * breaks the day the ordering changes.
     */
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
}

/**
 * The follow feed (HANDOFF §7).
 *
 * Tracks from the people you follow, newest first, assembled at read time -
 * including tracks you are only credited on, which is what makes following a
 * vocalist show you the records they sing on.
 */
export function FeedPage() {
  useDocumentTitle('Following');

  const feed = useFeed();

  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      <h1 className="text-2xl font-semibold tracking-tight text-ink">{copy['nav.following']}</h1>
      <FeedBody feed={feed} />
    </div>
  );
}

/**
 * The list itself, so the home page can render the same feed without
 * duplicating the empty states or the pagination.
 */
export function FeedBody({ feed }: { feed: ReturnType<typeof useFeed> }) {
  const play = usePlayer((state) => state.play);
  const current = usePlayer((state) => state.track);
  const playing = usePlayer((state) => state.playing);
  const toggle = usePlayer((state) => state.toggle);

  if (feed.isPending) {
    return (
      <div className="mt-6 flex flex-col gap-2" aria-busy="true">
        {[0, 1, 2, 3].map((i) => (
          <SkeletonRow key={i} />
        ))}
      </div>
    );
  }

  const pages = feed.data?.pages ?? [];
  const mode = pages[0]?.mode ?? 'following';
  const items = pages.flatMap((page) => page.items);

  if (items.length === 0) {
    return (
      <Card className="mt-6 p-6">
        <p className="text-sm text-soft">
          {mode === 'latest' ? (
            <>
              Nothing has been posted yet. When people start uploading, it shows up here.
            </>
          ) : (
            <>
              Quiet in here. The people you follow have not posted anything yet.{' '}
              <Link to="/discover" className="text-accent hover:underline">
                have a look at what is climbing
              </Link>
              .
            </>
          )}
        </p>
      </Card>
    );
  }

  return (
    <>
      {/*
        Said out loud when these are not people they follow.
        Quietly substituting strangers would make following look broken - the
        page would fill up whether or not it did anything.
      */}
      {mode === 'latest' && (
        <p className="mt-2 text-sm text-soft" data-feed-fallback>
          You are not following anyone yet, so this is the newest of everything.{' '}
          <Link to="/discover" className="text-accent hover:underline">
            {copy['nav.discover']}
          </Link>{' '}
          is a good place to find people.
        </p>
      )}

      <ul className="mt-6 flex flex-col gap-2" data-feed data-feed-mode={mode}>
        {items.map((track) => {
          const isCurrent = current?.id === track.id;

          return (
            <li key={track.id} data-feed-track={track.id} data-event={track.event.kind}>
              {/*
                Attribution above the row, the way every feed does it.
                Without it a rewind is indistinguishable from a release by the
                person you follow - which is both confusing and unfair to
                whoever actually made it.
              */}
              {track.event.kind === 'repost' && track.event.actor && (
                <p className="mb-1.5 flex items-center gap-1.5 pl-1 text-xs text-faint">
                  <RewindGlyph />
                  <Link to={`/${track.event.actor.username}`} className="hover:text-soft">
                    {track.event.actor.display_name || `@${track.event.actor.username}`}
                    {track.event.actor?.verified && <Verified />}
                  </Link>{' '}
                  rewound this
                </p>
              )}

              <Card className="flex items-center gap-3 p-3 sm:gap-4 sm:p-4">
                <button
                  type="button"
                  aria-label={isCurrent && playing ? `Pause ${track.title}` : `Play ${track.title}`}
                  onClick={() => (isCurrent ? toggle() : void play(track, items, 'feed'))}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--radius-pill)] bg-accent text-accent-ink transition-colors hover:bg-accent-hover"
                >
                  {isCurrent && playing ? (
                    <svg viewBox="0 0 16 16" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                      <rect x="4" y="2.5" width="3" height="11" rx="1" />
                      <rect x="9" y="2.5" width="3" height="11" rx="1" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 16 16" className="h-4 w-4 translate-x-px" fill="currentColor" aria-hidden="true">
                      <path d="M4.5 2.8v10.4a.6.6 0 0 0 .93.5l8-5.2a.6.6 0 0 0 0-1l-8-5.2a.6.6 0 0 0-.93.5Z" />
                    </svg>
                  )}
                </button>

                {track.artwork_small ? (
                  <img
                    src={track.artwork_small}
                    alt=""
                    loading="lazy"
                    className="h-11 w-11 shrink-0 rounded-[var(--radius-control)] object-cover"
                  />
                ) : (
                  <div
                    aria-hidden
                    className="h-11 w-11 shrink-0 rounded-[var(--radius-control)]"
                    style={{ backgroundImage: 'repeating-linear-gradient(45deg, var(--color-blank) 0 6px, var(--color-blank-alt) 6px 12px)' }}
                  />
                )}

                <div className="min-w-0 flex-1">
                  <DrawerLink
                    to={`/tracks/${track.id}`}
                    className="block truncate text-sm font-medium text-ink hover:underline"
                  >
                    {track.explicit && <Explicit className="mr-1.5 align-[-2px]" />}
                    {track.title}
                  </DrawerLink>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    {track.owner && (
                      <ArtistHoverCard username={track.owner.username}>
                        <Link to={`/${track.owner.username}`} className="meta-caps hover:text-ink">
                          {track.owner.display_name || `@${track.owner.username}`}
                          {track.owner?.verified && <Verified />}
                        </Link>
                      </ArtistHoverCard>
                    )}
                    {/* The event's time, not the track's. A rewind of a
                        two-year-old record is new to this feed today, and
                        showing the release date would make it look stale. */}
                    <span className="meta-caps">{age(track.event.at)}</span>
                    {track.event.kind === 'track' && isNew(track.event.at) && (
                      <Chip tone="fresh">{copy['label.newHere']}</Chip>
                    )}
                  </div>

                  {track.event.caption && (
                    <p className="mt-1.5 text-sm text-soft">“{track.event.caption}”</p>
                  )}
                </div>

                <FavoriteButton
                  trackId={track.id}
                  favorited={track.favorited}
                  likeCount={track.like_count}
                  size="sm"
                />

                <RepostButton
                  title={track.title}
                  artist={track.owner?.display_name ?? null}
                  trackId={track.id}
                  reposted={track.reposted}
                  repostCount={track.repost_count}
                  size="sm"
                />

                <AddToPlaylist trackId={track.id} title={track.title} size="sm" />
              </Card>
            </li>
          );
        })}
      </ul>

      {feed.hasNextPage && (
        <div className="mt-6 flex justify-center">
          <Button
            variant="secondary"
            loading={feed.isFetchingNextPage}
            onClick={() => void feed.fetchNextPage()}
            data-feed-more
          >
            Load more
          </Button>
        </div>
      )}
    </>
  );
}

/** "3 hours ago", "yesterday", "12 Aug". */
function age(iso: string): string {
  const then = new Date(iso.replace(' ', 'T') + 'Z');
  const hours = (Date.now() - then.getTime()) / 3_600_000;

  if (hours < 1) return 'just now';
  if (hours < 24) return `${Math.round(hours)}h ago`;
  if (hours < 48) return 'yesterday';
  if (hours < 24 * 7) return `${Math.round(hours / 24)}d ago`;

  return then.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function RewindGlyph() {
  return (
    <svg
      viewBox="0 0 20 20"
      className="h-3.5 w-3.5 shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 8.5V7a2.5 2.5 0 0 1 2.5-2.5H14M11.5 2L14 4.5l-2.5 2.5" />
      <path d="M16 11.5V13a2.5 2.5 0 0 1-2.5 2.5H6M8.5 18L6 15.5 8.5 13" />
    </svg>
  );
}

function isNew(iso: string): boolean {
  return Date.now() - new Date(iso.replace(' ', 'T') + 'Z').getTime() < 24 * 3_600_000;
}
